<template>
  <div :class="$style.wrapper">
    <DataTable
      v-if="(!isDisplayCreateForm && !hasItemsToDisplay) || hasItemsToDisplay"
      v-model:selection="selectedItems"
      :data-key="'id'"
      :value="itemsToDisplay"
      :row-class="disableRow"
    >
      <template v-if="selectedItems && selectedItems.length > 0" #header>
        <div :class="$style.selectionHeader" data-description="selection-info">
          <div>
            <p :class="$style.selectionText">
              {{ selectedItems.length }} item{{ selectedItems.length > 1 ? "s" : "" }} selected
            </p>
          </div>
          <div>
            <Button icon="pi pi-trash" label="Delete all" severity="secondary" outlined @click="handleDeleteBulk" />
          </div>
        </div>
      </template>
      <template v-if="hasItemsToDisplay && !loading">
        <Column :selection-mode="'multiple'"></Column>
        <Column field="sprite" header="Sprite" style="width: 20%">
          <template #body="slotProps">
            <div :class="$style.iapStatus">
              <VdTooltip v-if="slotProps.data.isPublished" orientation="top" :content="tooltipContent">
                <VdIcon store="fa" name="lock" />
              </VdTooltip>
              <SpriteSelector
                :options="sprites"
                :store-item-id="slotProps.data.id"
                :model-value="slotProps.data.sprite"
                :upload-in-progress="uploadSpriteLoading"
                no-border
                :disabled="slotProps.data.isPublished"
                @upload="handleUploadSprite"
                @link="handleLinkSprite"
                @delete="handleDeleteSprite"
                @unlink-sprite="handleUnLinkSprite"
              ></SpriteSelector>
            </div>
          </template>
        </Column>
        <Column field="name" header="Name" style="width: 35%">
          <template #body="slotProps">
            <div :class="$style.nameContainer">
              <span>{{ slotProps.data.name }}</span>
              <span :class="$style.technicalName">{{ slotProps.data.technicalName }}</span>
            </div>
          </template>
        </Column>
        <Column field="type" header="Type">
          <template #body="slotProps">
            <StoreItemTypeDropdown
              :type="slotProps.data.type"
              :has-border="false"
              :disabled="!isWIP || slotProps.data.isPublished"
              @change="(e) => handleEditType(slotProps.data.id, e, slotProps.data.sprite)"
            />
          </template>
        </Column>

        <Column header="Tags" style="width: 33%">
          <template #body="slotProps">
            <TagsManager
              :model-value="slotProps.data.tags"
              :item-id="slotProps.data.id"
              :game-id="gameId"
              :version-id="versionId"
              :max-visible-tags="2"
              :disabled="slotProps.data.isPublished"
              @update:model-value="(updatedTags) => handleTagUpdate(slotProps.data.id, updatedTags)"
            />
          </template>
        </Column>
        <Column header="Actions" style="width: 7%">
          <template #body="slotProps">
            <DropDownMenu :items="getItems(slotProps.data.id)" />
          </template>
        </Column>
      </template>

      <template v-if="shouldDisplayCreateForm" #footer>
        <div
          :class="$style.gridFooter"
          :style="{
            gridTemplateColumns: '18% 32% 17% 18% 15%',
          }"
        >
          <div :class="$style.footerCell">
            <SpriteSelector
              :model-value="newItem.sprite"
              :options="sprites"
              @select="handleSelectSprite"
              @upload="handleUploadSprite"
              @link="handleLinkSprite"
              @delete="handleDeleteSprite"
            ></SpriteSelector>
          </div>
          <div :class="$style.footerCell">
            <InputText
              :model-value="newItem.name"
              placeholder="Enter name"
              :class="`${$style.addInput} ${errors.name ? 'p-invalid' : ''}`"
              @update:model-value="handleNameInput"
              @blur="trimName"
            />
            <small v-if="errors.name" :class="$style.errorText">
              {{ errors.name }}
            </small>
          </div>
          <div :class="$style.footerCell">
            <StoreItemTypeDropdown :type="newItem.type" :is-wip="isWIP" @change="handleTypeChange" />
          </div>
          <div :class="$style.footerCell">
            <TagsInput v-model="newItem.tags" :tags="gameTags" :game-id="gameId" :version-id="versionId" />
          </div>
          <div :class="$style.footerCell">
            <Button icon="pi pi-times" severity="secondary" outlined @click="handleResetForm" />
            <Button icon="pi pi-check" @click="handleAddItem" />
          </div>
        </div>
      </template>
    </DataTable>

    <div v-if="loading">Loading...</div>
    <div v-if="!loading && !hasItemsToDisplay" :class="$style.emptyState">
      <VdIcon store="fa" name="box-open" :class="$style.emptyStateIcon" custom-size="100px" color="#8ec7ff" />
      <p :class="$style.emptyStateText">No items to display.</p>
    </div>
  </div>
  <Button
    v-if="!shouldDisplayCreateForm && isWIP"
    :class="$style.addItemButton"
    label="Add Item"
    icon="pi pi-plus"
    @click="displayCreateForm"
  />
  <DuplicateDialog
    :is-visible="isDuplicateDialogVisible"
    :selected-item="itemToDuplicate"
    @update:is-visible="(value) => (isDuplicateDialogVisible = value)"
    @cancel="closeDuplicateDialog"
  />
</template>

<script setup>
import VdIcon from "@/components/VdIcon/VdIcon.vue";
import DuplicateDialog from "./components/DuplicateDialog.vue";

import { computed, reactive, ref, watch } from "vue";
import { useItems } from "./hooks/useItems";

import DropDownMenu from "@/components/DropDownMenu";
import SpriteSelector from "@/components/SpriteSelector";
import TagsInput from "@/components/TagsInput";
import TagsManager from "@/components/TagsManager";
import useSprites from "@/modules/Items/useSprites";
import { VdTooltip } from "@voodoo.io/voodoo-ui";
import Button from "primevue/button";
import Column from "primevue/column";
import DataTable from "primevue/datatable";
import InputText from "primevue/inputtext";
import { useConfirm } from "primevue/useconfirm";
import { useRoute } from "vue-router";
import StoreItemTypeDropdown from "./components/StoreItemTypeDropdown.vue";
import useTags from "./hooks/useTags";

const route = useRoute();
const gameId = ref(route.params.gameId);
const versionId = ref(route.params.versionId);

const spriteType = "ITEM";

const tooltipContent =
  "Once published, the IAP will be locked and associations with<br />different non-consumables won’t be possible. <a href='https://www.notion.so/voodoo/Supporting-Non-Consumables-with-Offers-Shop-16fa0b481db480729a96d15ba772945d' target='_blank' rel='noopener noreferrer'>More info</a>";

const { itemsToDisplay, addItem, loading, deleteItems, updateTags, updateType, updateSprite } = useItems(
  gameId.value,
  versionId.value
);

const isWIP = computed(() => versionId.value === "wip");
const hasItemsToDisplay = ref(false);

watch(itemsToDisplay, (newItems) => {
  hasItemsToDisplay.value = newItems.length > 0;
});

const isDisplayCreateForm = ref(false);
const selectedItems = ref([]);
const confirm = useConfirm();

const shouldDisplayCreateForm = computed(() => !hasItemsToDisplay.value || isDisplayCreateForm.value);

const displayCreateForm = () => {
  isDisplayCreateForm.value = true;
};

const { tags: gameTags, createTagsBulk, refetchTags } = useTags(gameId.value);

const { sprites, uploadSprite, deleteSprite, uploadSpriteLoading, reFetchSprites } = useSprites(
  gameId.value,
  versionId.value,
  spriteType
);

const newItem = reactive({
  sprite: null,
  name: "",
  tags: [],
  type: "CONSUMABLE",
});

const errors = reactive({
  name: null,
  sprite: null,
  all: null,
  type: null,
});

const handleResetForm = () => {
  newItem.tags = [];
  newItem.name = "";
  newItem.sprite = null;
  newItem.type = "CONSUMABLE";

  errors.name = null;
  errors.sprite = null;
  errors.all = null;
  errors.type = null;

  isDisplayCreateForm.value = false;
};

const handleSelectSprite = (sprite) => {
  newItem.sprite = sprite;
};

const handleTypeChange = (type) => {
  newItem.type = type;
};

const handleEditType = async (itemId, type) => {
  const itemToUpdate = itemsToDisplay.value.find((storeItem) => storeItem.id === itemId);
  await updateType(itemId, type, itemToUpdate.sprite?.id);
};

const validateNewItem = () => {
  errors.name = null;
  errors.sprite = null;
  errors.all = null;

  let isValid = true;

  if (!newItem.name) {
    errors.name = "Name is required.";
    isValid = false;
  } else {
    trimName();
    const nameRegex = /^[A-Za-z][A-Za-z0-9_]*$/;
    if (!nameRegex.test(newItem.name)) {
      errors.name = "Name should be alphanumeric and start with a letter.";
      isValid = false;
    }
  }

  return isValid;
};

const handleAddItem = async () => {
  if (!validateNewItem()) {
    return;
  }

  try {
    const newTags = newItem.tags.filter((tag) => !tag.id).map((tag) => ({ name: tag.name }));
    const existingTags = newItem.tags.filter((tag) => tag.id);
    let processedTags = existingTags;

    if (newTags.length > 0) {
      const createdTags = await createTagsBulk(newTags);
      processedTags = [...processedTags, ...createdTags];
    }

    await addItem({
      sprite: newItem.sprite?.id || null,
      name: newItem.name.trim(),
      tags: processedTags.map((tag) => tag.id),
      type: newItem.type,
    });
    await reFetchSprites();

    handleResetForm();
    refetchTags();
  } catch (error) {
    if (error.message === "Request failed with status code 409") {
      errors.name = "This name is already taken.";
    } else {
      errors.all = "An error occurred while adding the item.";
    }
  }
};

const handleDelete = async (itemsToDelete) => {
  confirm.require({
    message: `You will delete ${itemsToDelete.length} item${
      itemsToDelete.length > 1 ? "s" : ""
    }. Are you sure you want to continue?`,
    header: "Confirmation",
    rejectClass: "p-button-secondary p-button-outlined",
    acceptClass: "p-button-danger",
    rejectLabel: "Cancel",
    acceptLabel: "Delete",
    accept: async () => {
      await deleteItems(itemsToDelete);
      await reFetchSprites();
      selectedItems.value = [];
    },
  });
};

const handleDeleteBulk = async () => {
  await handleDelete(selectedItems.value.map((item) => item.id));
  await reFetchSprites();
};

const handleDeleteItem = async (id) => {
  if (!id) {
    return;
  }
  await handleDelete([id]);
};

const handleTagUpdate = async (itemId, updatedTags) => {
  const updatedTagIds = updatedTags.map((tag) => tag.id);
  const itemToUpdate = itemsToDisplay.value.find((storeItem) => storeItem.id === itemId);
  await updateTags(itemId, updatedTagIds, itemToUpdate.sprite?.id);
};

const isDuplicateDialogVisible = ref(false);
const itemToDuplicate = ref(null);

const openDuplicateDialog = (item) => {
  itemToDuplicate.value = item;
  isDuplicateDialogVisible.value = true;
};

const closeDuplicateDialog = () => {
  isDuplicateDialogVisible.value = false;
};

const getItems = (id) => [
  {
    label: "Duplicate",
    icon: "pi pi-copy",
    command: async () => {
      const item = itemsToDisplay.value.find((storeItem) => storeItem.id === id);
      openDuplicateDialog(item);
    },
    disabled: !isWIP.value,
  },
  {
    label: "Delete",
    icon: "pi pi-trash",
    command: async () => {
      await handleDeleteItem(id);
    },
    disabled: !isWIP.value || itemsToDisplay.value.find((item) => item.id === id).isPublished,
  },
];

const disableRow = (data) => {
  if (data.isPublished) {
    return "iap-row-disabled";
  }

  return "";
};

const handleUploadSprite = async (event) => {
  await uploadSprite(event.files, spriteType);
};

function handleNameInput(value) {
  newItem.name = value.trimStart();
}

function trimName() {
  newItem.name = newItem.name.trim();
}

const handleDeleteSprite = (id, spriteName) => {
  confirm.require({
    message: `Are you sure you want to delete the Sprite ${spriteName}?`,
    header: "Confirmation",
    rejectClass: "p-button-secondary p-button-outlined",
    acceptClass: "p-button-danger",
    acceptIcon: "pi pi-trash",
    rejectLabel: "Cancel",
    acceptLabel: "Delete",
    accept: async () => {
      await deleteSprite(id);
    },
  });
};

const handleLinkSprite = async (storeItemId, id) => {
  await updateSprite(storeItemId, id);
  // Have to refetch sprites to get the updated list of sprites the user can use
  await reFetchSprites();
};

const handleUnLinkSprite = async (storeItemId) => {
  await updateSprite(storeItemId);
  // Have to refetch sprites to get the updated list of sprites the user can use
  await reFetchSprites();
};
</script>

<style module lang="scss">
.p-listbox {
  border: none;
}

.selectionText {
  font-weight: 400;
  font-size: 14px;
  line-height: 16px;
}

.gridFooter {
  display: grid;

  width: 100%;
  box-sizing: border-box;
}

.footerCell:last-child > * {
  margin-right: 10px;
}

.footerCell:not(:first-child) {
  padding-left: 1rem;
}

.addInput {
  width: 100%;
}

.addInput ul {
  width: 100%;
}

.headerDescription {
  color: #909298;
  margin-top: 0.5rem;
}

.nameContainer {
  display: flex;
  flex-direction: column;

  .technicalName {
    color: #909298;
  }
}

.sprite {
  width: 30px;
  height: 30px;
}

.multiselect {
  width: 100%;
  display: flex;
}

.errorText {
  color: #e24c4c;
}

.addItemButton {
  margin-top: 1rem;
}

.emptyState {
  padding-top: 3rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.emptyStateText {
  font-size: 1.5rem;
  font-weight: 500;
  margin-top: 1rem;
  color: var(--vd-color-blue-light-4);
}

.selectionHeader {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.iapStatus {
  display: flex;
  align-items: center;
  gap: 4px;
}
</style>

<style scoped>
:deep(.p-datatable-table-container) {
  overflow: visible !important;
}

:deep(.iap-row-disabled) {
  opacity: 0.5;
}
</style>
